import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import Pagination from '../../components/Pagination.tsx';
import layoutStyles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import reportStyles from '../../styles/ReportesIngresosDiarios.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import { getCampus } from '../../api/campus';
import { fetchEstudiantesBecas } from '../../api/estudiantesBecas';
import { exportReporteBecasXLS } from '../../utils/exportReporteBecasXLS';

const ReporteBecasScreen = () => {
  const { isCollapsed } = useSidebar();
  const currentYear = String(new Date().getFullYear());
  const [filters, setFilters] = useState({
    campus: '',
    seccion: '',
    programa: '',
    tipo_beca: '',
    anio: currentYear,
    ciclo_escolar: '',
  });
  const [rows, setRows] = useState([]);
  const [catalogRows, setCatalogRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [campusList, setCampusList] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Cargar lista de campus una sola vez para resolver nombre→ID
  useEffect(() => {
    let mounted = true;
    getCampus().catch(() => []).then(cam => {
      if (mounted) setCampusList(Array.isArray(cam) ? cam : []);
    });
    return () => { mounted = false; };
  }, []);

  // Sincronizar filters.campus con el header global (usa ID numérico)
  useEffect(() => {
    if (!campusList.length) return;
    
    const syncCampusFromHeader = () => {
      // Obtener del event custom o del localStorage
      const campusValue = localStorage.getItem('selectedCampus');
      
      if (!campusValue) {
        setFilters(prev => ({ ...prev, campus: '' }));
        return;
      }
      
      // Intentar resolver el nombre del campus a su ID
      const found = campusList.find(c => 
        c.nombre === campusValue || String(c.id) === campusValue
      );
      
      const campusId = found ? String(found.id) : campusValue;
      setFilters(prev => ({ ...prev, campus: campusId }));
    };
    
    // Sincronizar inicial
    syncCampusFromHeader();
    
    // Escuchar cambios del header
    const handler = () => syncCampusFromHeader();
    window.addEventListener('campusChanged', handler);
    window.addEventListener('storage', handler);
    
    return () => {
      window.removeEventListener('campusChanged', handler);
      window.removeEventListener('storage', handler);
    };
  }, [campusList]);
  const seccionesUnicas = useMemo(() => {
    const map = new Map();

    catalogRows.forEach((row) => {
      const id = row.id_seccion;
      if (id === null || id === undefined || id === '') return;
      const key = String(id);
      if (!map.has(key)) {
        map.set(key, row.seccion_nombre || `Seccion ${key}`);
      }
    });

    return Array.from(map.entries())
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [catalogRows]);

  const programasUnicos = useMemo(() => {
    const map = new Map();

    catalogRows.forEach((row) => {
      const id = row.id_programa_actual;
      if (id === null || id === undefined || id === '') return;
      const key = String(id);
      if (!map.has(key)) {
        map.set(key, row.programa_nombre || `Programa ${key}`);
      }
    });

    return Array.from(map.entries())
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [catalogRows]);

  const aniosUnicos = useMemo(() => {
    const unique = new Set(
      catalogRows
        .map((row) => String(row.fecha_inicio_beca || '').trim().slice(0, 4))
        .filter((anio) => /^\d{4}$/.test(anio))
    );

    unique.add(currentYear);

    return Array.from(unique).sort((a, b) => Number(b) - Number(a));
  }, [catalogRows, currentYear]);

  const tiposBecaUnicos = useMemo(() => {
    const unique = new Set(
      catalogRows
        .map((row) => String(row.tipo_beca || '').trim())
        .filter(Boolean)
    );

    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'es'));
  }, [catalogRows]);

  // El filtrado por año y tipo_beca ahora se realiza en el backend
  // Esta línea mantiene compatibilidad pero NO filtra en memoria
  const filteredRows = useMemo(() => {
    return rows;
  }, [rows]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.costoColegiatura += Number(row.costo_colegiatura ?? 0) || 0;
        acc.cantidadPagaEstudiante += Number(row.cantidad_paga_estudiante ?? 0) || 0;
        acc.cantidadPagoAnual += Number(row.cantidad_pago_anual ?? 0) || 0;
        return acc;
      },
      {
        costoColegiatura: 0,
        cantidadPagaEstudiante: 0,
        cantidadPagoAnual: 0,
      }
    );
  }, [filteredRows]);

  const getSortableValue = (row, key) => {
    switch (key) {
      case 'matricula':
        return String(row.matricula || '').trim().toLowerCase();
      case 'nombre_estudiante':
        return String(row.nombre_estudiante || '').trim().toLowerCase();
      case 'tipo_beca':
        return String(row.tipo_beca || '').trim().toLowerCase();
      case 'nombre_beca':
        return String(row.nombre_beca || '').trim().toLowerCase();
      case 'porcentaje_beca':
        return Number(row.porcentaje_beca ?? 0) || 0;
      case 'monto_beca':
        return Number(row.monto_beca ?? 0) || 0;
      case 'ciclo_escolar':
        return String(row.ciclo_escolar || '').trim().toLowerCase();
      case 'fecha_inicio_beca':
        return Date.parse(row.fecha_inicio_beca || '') || 0;
      case 'fecha_termino_beca':
        return Date.parse(row.fecha_termino_beca || '') || 0;
      case 'costo_colegiatura':
        return Number(row.costo_colegiatura ?? 0) || 0;
      case 'cantidad_paga_estudiante':
        return Number(row.cantidad_paga_estudiante ?? 0) || 0;
      case 'cantidad_pago_anual':
        return Number(row.cantidad_pago_anual ?? 0) || 0;
      default:
        return '';
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;

    const sorted = [...filteredRows].sort((a, b) => {
      const valueA = getSortableValue(a, sortConfig.key);
      const valueB = getSortableValue(b, sortConfig.key);

      if (valueA < valueB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredRows, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [page, pageSize, sortedRows]);

  useEffect(() => {
    setPage(1);
  }, [filters, sortConfig]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleExportXLS = useCallback(() => {
    if (!sortedRows || sortedRows.length === 0) {
      return;
    }
    exportReporteBecasXLS({
      resultados: sortedRows,
      anio: filters.anio,
      tipoBeca: filters.tipo_beca,
    });
  }, [sortedRows, filters.anio, filters.tipo_beca]);

  useEffect(() => {

    const loadCatalogData = async () => {
      try {
        const response = await fetchEstudiantesBecas({
          campus: filters.campus,
          seccion: '',
          programa: '',
          tipo_beca: '',
          anio: '',
          ciclo_escolar: '',
        });
        const results = Array.isArray(response) ? response : response?.results;
        setCatalogRows(Array.isArray(results) ? results : []);
      } catch {
        setCatalogRows([]);
      }
    };

    loadCatalogData();
  }, [filters.campus]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await fetchEstudiantesBecas(filters);
        const results = Array.isArray(response) ? response : response?.results;
        setRows(Array.isArray(results) ? results : []);
      } catch (err) {
        console.error('Error al cargar reporte de becas:', err);
        setRows([]);
        setError('No se pudo cargar la informacion del reporte de becas.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [filters]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        key,
        direction: 'asc',
      };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <span style={{ marginLeft: 6, opacity: 0.35 }}>↕</span>;
    }

    return sortConfig.direction === 'asc'
      ? <span style={{ marginLeft: 6 }}>↑</span>
      : <span style={{ marginLeft: 6 }}>↓</span>;
  };

  const formatPorcentajeBeca = (value) => {
    if (value === null || value === undefined) return '';
    const text = String(value).trim();
    if (!text) return '';
    if (text.includes('%')) return text;
    return `${text} %`;
  };

  const formatMoney = (value) => {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) return '$0.00';
    return `$${amount.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className={layoutStyles.mainContainer} style={{ height: '100vh', display: 'flex' }}>
      <div
        className={`${layoutStyles.layout} ${isCollapsed ? layoutStyles.sidebarCollapsedLayout : ''}`}
        style={{ flex: 1, display: 'flex', height: '100vh' }}
      >
        <div className={layoutStyles.sidebarColumn} style={{ height: '100vh', minHeight: 0 }}>
          <Sidebar />
        </div>
        <div
          className={layoutStyles.contentColumn}
          style={{ position: 'relative', height: '100vh', overflowY: 'auto', minHeight: 0 }}
        >
          <HeaderGlobal title="Reporte de Becas" campus={filters.campus} />
          <main className={layoutStyles.mainContent}>
            <div className={layoutStyles.contentArea}>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Filtros estilo rectoría */}
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={estudiantesStyles.searchRow}>

                      {/* Filtro Sección */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Sección</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          name="seccion"
                          value={filters.seccion}
                          onChange={handleFilterChange}
                        >
                          <option value="">Todas</option>
                          {seccionesUnicas.map((seccion) => (
                            <option key={seccion.id} value={seccion.id}>{seccion.nombre}</option>
                          ))}
                        </select>
                      </div>
                      {/* Filtro Programa */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Programa</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          name="programa"
                          value={filters.programa}
                          onChange={handleFilterChange}
                        >
                          <option value="">Todos</option>
                          {programasUnicos.map((programa) => (
                            <option key={programa.id} value={programa.id}>{programa.nombre}</option>
                          ))}
                        </select>
                      </div>
                      {/* Filtro Año */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Año</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          name="anio"
                          value={filters.anio}
                          onChange={handleFilterChange}
                        >
                          {aniosUnicos.map((anio) => (
                            <option key={anio} value={anio}>{anio}</option>
                          ))}
                        </select>
                      </div>
                      {/* Filtro Tipo de beca */}
                      <div className={estudiantesStyles.formGroup}>
                        <label className={estudiantesStyles.searchLabel}>Tipo de beca</label>
                        <select
                          className={estudiantesStyles.searchInput}
                          name="tipo_beca"
                          value={filters.tipo_beca}
                          onChange={handleFilterChange}
                        >
                          <option value="">Todos</option>
                          {tiposBecaUnicos.map((tipoBeca) => (
                            <option key={tipoBeca} value={tipoBeca}>{tipoBeca}</option>
                          ))}
                        </select>
                      </div>
                      {/* Limpiar */}
                      <div className={reportStyles.filterActions}>
                        <button
                          type="button"
                          className={reportStyles.clearFiltersButton}
                          onClick={() => setFilters(prev => ({ campus: prev.campus, seccion: '', programa: '', tipo_beca: '', anio: currentYear, ciclo_escolar: '' }))}
                        >
                          Limpiar filtros
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={reportStyles.actionsBar}>
                  <button
                    type="button"
                    className={`${reportStyles.downloadButton} bg-blue-900 text-white border-transparent`}
                    onClick={handleExportXLS}
                    disabled={sortedRows.length === 0}
                    title={sortedRows.length > 0 ? 'Descargar Excel' : 'No hay datos para exportar'}
                    style={{
                      backgroundColor: '#1e3a8a',
                      color: '#ffffff',
                      borderColor: 'transparent',
                      opacity: sortedRows.length === 0 ? 0.6 : 1,
                      cursor: sortedRows.length === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <span className={reportStyles.downloadIcon}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 3v12m0 0 4-4m-4 4-4-4"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5 15v3a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span>Descargar Excel</span>
                  </button>
                </div>
                {/* Tabla */}
                <div className={reportStyles.reportContainer}>
                  {error ? (
                    <div style={{ color: '#b42318', marginBottom: 12 }}>{error}</div>
                  ) : null}
                  <table className={reportStyles.dataTable}>
                    <thead>
                      <tr style={{ background: '#1e3a8a', color: '#ffffff' }}>
                        <th onClick={() => handleSort('matricula')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Matrícula{renderSortIcon('matricula')}
                        </th>
                        <th onClick={() => handleSort('nombre_estudiante')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Nombre del estudiante{renderSortIcon('nombre_estudiante')}
                        </th>
                        <th onClick={() => handleSort('tipo_beca')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Tipo de beca{renderSortIcon('tipo_beca')}
                        </th>
                        <th onClick={() => handleSort('nombre_beca')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Nombre de beca{renderSortIcon('nombre_beca')}
                        </th>
                        <th onClick={() => handleSort('porcentaje_beca')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Porcentaje de beca{renderSortIcon('porcentaje_beca')}
                        </th>
                        <th onClick={() => handleSort('monto_beca')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Monto de Beca{renderSortIcon('monto_beca')}
                        </th>
                        <th onClick={() => handleSort('ciclo_escolar')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Ciclo de inicio de Beca{renderSortIcon('ciclo_escolar')}
                        </th>
                        <th onClick={() => handleSort('fecha_inicio_beca')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Fecha de inicio de la beca{renderSortIcon('fecha_inicio_beca')}
                        </th>
                        <th onClick={() => handleSort('fecha_termino_beca')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Fecha de término de la beca{renderSortIcon('fecha_termino_beca')}
                        </th>
                        <th onClick={() => handleSort('costo_colegiatura')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Costo de la colegiatura (parcialidad){renderSortIcon('costo_colegiatura')}
                        </th>
                        <th onClick={() => handleSort('cantidad_paga_estudiante')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Cantidad que paga el estudiante{renderSortIcon('cantidad_paga_estudiante')}
                        </th>
                        <th onClick={() => handleSort('cantidad_pago_anual')} style={{ cursor: 'pointer', color: '#ffffff', fontWeight: '600' }}>
                          Cantidad que pagó en un año el estudiante{renderSortIcon('cantidad_pago_anual')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={12} style={{ textAlign: 'center', color: '#777', padding: 16 }}>
                            Cargando informacion...
                          </td>
                        </tr>
                      ) : sortedRows.length === 0 ? (
                        <tr>
                          <td colSpan={12} style={{ textAlign: 'center', color: '#777', padding: 16 }}>
                            Sin información para mostrar.
                          </td>
                        </tr>
                      ) : (
                        paginatedRows.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.matricula}</td>
                            <td>{row.nombre_estudiante}</td>
                            <td>{row.tipo_beca}</td>
                            <td>{row.nombre_beca}</td>
                            <td>{formatPorcentajeBeca(row.porcentaje_beca)}</td>
                            <td>{row.monto_beca ?? 0}</td>
                            <td>{row.ciclo_escolar}</td>
                            <td>{row.fecha_inicio_beca}</td>
                            <td>{row.fecha_termino_beca}</td>
                            <td>{formatMoney(row.costo_colegiatura)}</td>
                            <td>{formatMoney(row.cantidad_paga_estudiante)}</td>
                            <td>{formatMoney(row.cantidad_pago_anual)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 700 }}>
                        <td>TOTAL GENERAL</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className={reportStyles.numeric} style={{ textAlign: 'left' }}>
                          {formatMoney(totals.costoColegiatura)}
                        </td>
                        <td className={reportStyles.numeric} style={{ textAlign: 'left' }}>
                          {formatMoney(totals.cantidadPagaEstudiante)}
                        </td>
                        <td className={reportStyles.numeric} style={{ textAlign: 'left' }}>
                          {formatMoney(totals.cantidadPagoAnual)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>

                  {totalPages > 1 && (
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center' }}>
                      <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
                    </div>
                  )}
                </div>
                {/* Aquí puedes agregar una tabla/resumen para el corte por tipo de beca */}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ReporteBecasScreen;
